import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useT } from "../i18n";
import { Icon } from "./Icon";

/** Popover width in px (CSS: min(320px, 100vw - 32px)). */
const POP_WIDTH = 320;

/**
 * Short contextual help next to a concept (ADR 0029): a small "?" button that opens a popover with the
 * definition. Disclosure pattern: the button is a normal tab stop with aria-expanded, Enter/Space toggles,
 * Escape or a click outside closes and focus returns to the button. The text is static, so it is also
 * read in full by screen readers when opened (role="note", linked with aria-controls).
 * Never put it inside a link or another button: it is a button itself.
 */
export function HelpTip({ topic, children, align = "end" }: { topic: string; children: ReactNode; align?: "start" | "end" }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<"start" | "end" | "fit">(align);
  const [shift, setShift] = useState(0);
  const id = useId();
  const box = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = t("help.about", { topic });
  return (
    <span className="help-tip" ref={box}>
      <button
        ref={button}
        type="button"
        className="help-tip-button"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        title={label}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Open towards the side with room (a tile at the left edge would push the popover off-screen).
          const r = button.current?.getBoundingClientRect();
          if (r && typeof window !== "undefined") {
            const vw = window.innerWidth;
            const w = Math.min(POP_WIDTH, vw - 32);
            // Stay inside the content area (not over the menu rail) when there is one.
            const area = button.current?.closest("main")?.getBoundingClientRect();
            const minX = area && area.width > 0 ? area.left : 0;
            const maxX = area && area.width > 0 ? area.right : vw;
            const endFits = r.right - w >= minX + 8;
            const startFits = r.left + w <= maxX - 8;
            if (align === "end" ? endFits : startFits) setSide(align);
            else if (align === "end" ? startFits : endFits) setSide(align === "end" ? "start" : "end");
            else {
              // Narrow screen: neither side has room, pin the popover 16 px from the content edge.
              setSide("fit");
              setShift(minX + 16 - r.left);
            }
          }
          setOpen((o) => !o);
        }}
      >
        <Icon name="help" size={15} />
      </button>
      {open && (
        <span id={id} role="note" className={`help-tip-pop help-tip-${side}`} style={side === "fit" ? { left: shift } : undefined}>
          <span className="help-tip-title">{topic}</span>
          <span className="help-tip-body">{children}</span>
        </span>
      )}
    </span>
  );
}
